/**
 * Shared Lifecycle Module — Edge Functions ve Frontend/Testler için merkezi döngü ilerletme mantığı
 * Single Source of Truth (SSOT): Tüm bootstrap, catch-up ve over-advance kararları bu modülden geçer.
 */

export interface LifecycleMember {
  /** Üye satırı; misafir koltuğu (user_id boş) ödemeyle eşleştirmek için */
  id?: string
  user_id: string | null
  turn_index: number
  status: string
}

export interface LifecycleTransaction {
  id?: string
  sender_id: string | null
  sender_member_id?: string | null
  cycle_number: number
  status: string
}

export interface CatchUpParams {
  currentCycle: number
  currentCycleDate: string | null
  startDate: string
  cyclePeriod: 'monthly' | 'weekly'
  memberCount: number
  nowDate: Date
  members: LifecycleMember[]
  transactions: LifecycleTransaction[]
  /**
   * FAZ 3 — Taşınan günde takibin başladığı tur. Bundan önceki turlar
   * "defterde kaldı": ilerletilmez, ceza yazılmaz. Verilmezse 1.
   */
  trackingStartCycle?: number
  /**
   * FAZ 3 — Taşıma tarihi (YYYY-MM-DD). Ödeme günü bu tarihten önceyse
   * gecikme payı taşıma tarihinden sayılır. Verilmezse etkisiz.
   */
  penaltyFreeBefore?: string | null
}

export interface CatchUpPenalty {
  cycleNumber: number
  userId: string
  penalty: number
  trigger: 'force_advance_late' | 'force_advance_very_late'
}

export interface CatchUpResult {
  currentCycle: number
  currentCycleDate: string
  advancedCyclesCount: number
  isCompleted: boolean
  penalties: CatchUpPenalty[]
  idsToSkip: string[]
  /**
   * Zorla ilerletilen turlarda ödemeyen misafir koltuklar (puanı yok, bilgi amaçlı).
   */
  unpaidGuestMemberIds?: string[]
}

const GUN_MS = 24 * 60 * 60 * 1000

/** Verilen ay UTC'de kaç gün sürer. */
function ayinGunSayisi(yil: number, ayIndeksi: number): number {
  // Bir sonraki ayın 0. günü = bu ayın son günü.
  return new Date(Date.UTC(yil, ayIndeksi + 1, 0)).getUTCDate()
}

/**
 * Tarih hem ayrıştırılabilir hem de TAKVİMDE VAR olmalı.
 *
 * `new Date('2026-02-30T00:00:00Z')` hata vermez, 2 Mart'a normalleşir —
 * bu yüzden yalnız `isNaN` kontrolü yetmiyordu ve fonksiyon 30 Şubat'a
 * `true` diyordu. Çözüm: geri okuma. Biçimlendirilmiş tarih girdiyle
 * birebir aynı değilse gün gerçek değildir.
 *
 * Ayrı bir bicim regex'i de yazmistim; mutasyon testinde hicbir testi
 * kirmadi. Hakliydi: geri okuma bicim bozukluklarini da yakaliyor
 * ('2026-2-5' -> '2026-02-05' esit degil). Olu koruma kaldirildi.
 */
export function isValidCycleDate(date: string | null | undefined): boolean {
  if (!date) return false
  const d = new Date(date + 'T00:00:00Z')
  if (isNaN(d.getTime())) return false
  if (d.getUTCFullYear() < 2024) return false
  return d.toISOString().split('T')[0] === date
}

/**
 * Döngü tarihini bir sonraki döneme ilerletir.
 *
 * AYLIK: ayın günü korunur, ama hedef ayda o gün yoksa ayın SON gününe
 * kırpılır. Eskiden `setUTCMonth` taşmayı normalleştiriyordu ve
 * 31 Ocak +1 ay = 3 Mart oluyordu; Nisan gibi 30 günlük aylar tamamen
 * atlanabiliyordu.
 *
 * `anchorDay` verilirse ASIL gün odur: Şubat'ta 28'e kırpılan bir grup
 * Mart'ta yeniden 31'ine döner. Verilmezse mevcut tarihin günü çapa
 * sayılır — o durumda kırpma kalıcı olur, çünkü fonksiyonun grubun asıl
 * gününü bilmesinin başka yolu yoktur.
 *
 * HAFTALIK: tam 7 gün eklenir, çapa yok sayılır.
 */
export function advanceCycleDate(
  currentCycleDate: string,
  cyclePeriod: 'monthly' | 'weekly',
  anchorDay?: number
): string {
  const date = new Date(currentCycleDate + 'T00:00:00Z')

  if (cyclePeriod === 'weekly') {
    date.setUTCDate(date.getUTCDate() + 7)
    return date.toISOString().split('T')[0]
  }

  const hedefYil = date.getUTCFullYear() + (date.getUTCMonth() === 11 ? 1 : 0)
  const hedefAy = (date.getUTCMonth() + 1) % 12
  // Gecersiz capa (0, negatif, sayi degil) 'verilmemis' sayilir; 0'i
  // 1'e kirpmak grubun odeme gununu sessizce ayin basina tasirdi.
  const capaGecerli = typeof anchorDay === 'number' && Number.isFinite(anchorDay) && anchorDay >= 1
  const istenenGun = capaGecerli ? anchorDay : date.getUTCDate()
  const guvenliGun = Math.max(1, Math.min(istenenGun, ayinGunSayisi(hedefYil, hedefAy)))

  return new Date(Date.UTC(hedefYil, hedefAy, guvenliGun)).toISOString().split('T')[0]
}

export function calculateLateDays(declaredAt: Date, cycleDate: string): number {
  const cycle = new Date(cycleDate + 'T23:59:59Z')
  const diffMs = declaredAt.getTime() - cycle.getTime()
  if (diffMs <= 0) return 0
  return Math.ceil(diffMs / GUN_MS)
}

/**
 * Bir grubun N. döneminin ödeme günü.
 *
 * `cycleNumber` 1 TABANLI: 1. dönem başlangıç tarihinin kendisidir.
 *
 * Bu hesap depoda ALTI ayrı yerde yazılıydı (Groups sayfası,
 * ManualOrderEditor, utils/abacus/date, execute-draw,
 * reputation) ve hepsi `setMonth` kullandığı için ay sonunda taşıyordu.
 * Artık tek kaynak burası; hepsi buradan türer.
 *
 * Başlangıç günü çapadır: ayın 31'inde başlayan grup Şubat'ta 28'e
 * kırpılır ama Mart'ta yeniden 31'ine döner.
 *
 * Geçersiz başlangıç tarihinde BOŞ DİZE döner — sessizce bugüne ya da
 * uydurma bir tarihe düşmez; çağıran taraf eksikliği görmelidir.
 */
export function cycleDueDate(
  startDate: string,
  cycleNumber: number,
  cyclePeriod: 'monthly' | 'weekly'
): string {
  if (!isValidCycleDate(startDate)) return ''

  const adimSayisi = Math.max(0, Math.floor(cycleNumber) - 1)
  if (adimSayisi === 0) return startDate

  const capaGun = new Date(startDate + 'T00:00:00Z').getUTCDate()
  let d = startDate
  for (let i = 0; i < adimSayisi; i++) {
    d = advanceCycleDate(d, cyclePeriod, capaGun)
  }
  return d
}

/**
 * FIX A — Bootstrap
 * Status active / draw sealed olduğunda current_cycle_date null ise start_date'e ayarlar.
 */
export function bootstrapGroupCycleDate(
  startDate: string,
  currentCycleDate: string | null | undefined
): string {
  if (currentCycleDate && isValidCycleDate(currentCycleDate)) {
    return currentCycleDate
  }
  return startDate
}

/**
 * FIX B — İlerleme (Catch-up + Over-advance kilidi)
 * Pure, UTC-safe ve nowDate parametreli döngü ilerletme fonksiyonu.
 */
export function calculateCatchUpAdvancement(params: CatchUpParams): CatchUpResult {
  const {
    startDate,
    cyclePeriod,
    memberCount,
    nowDate,
    members,
    transactions,
  } = params

  // FAZ 3: takip başlangıcından önceki turlar hiç işlenmez (defterde kaldı).
  const trackingStartCycle = Math.max(1, Math.floor(params.trackingStartCycle ?? 1))
  const penaltyFreeBefore = isValidCycleDate(params.penaltyFreeBefore) ? params.penaltyFreeBefore : null
  let currentCycle = Math.max(params.currentCycle, trackingStartCycle)
  let currentCycleDate = bootstrapGroupCycleDate(startDate, params.currentCycleDate)
  let advancedCyclesCount = 0
  let isCompleted = false
  const penalties: CatchUpPenalty[] = []
  const idsToSkip: string[] = []
  const unpaidGuestMemberIds: string[] = []

  const graceDays = cyclePeriod === 'monthly' ? 7 : 2

  // Grubun ASIL odeme gunu baslangic tarihinden gelir. Capa olmadan,
  // Subat'ta 28'e kirpilan bir grup 31'ine bir daha donemezdi.
  const capaGun = isValidCycleDate(startDate)
    ? new Date(startDate + 'T00:00:00Z').getUTCDate()
    : undefined

  while (currentCycle <= memberCount) {
    if (!isValidCycleDate(currentCycleDate)) {
      currentCycleDate = startDate
    }

    // FAZ 3: ödeme günü taşımadan önceyse gecikme payı taşıma gününden sayılır.
    const effectiveDueDate =
      penaltyFreeBefore && penaltyFreeBefore > currentCycleDate ? penaltyFreeBefore : currentCycleDate
    const dueDate = new Date(effectiveDueDate + 'T00:00:00Z')
    const forceDate = new Date(dueDate.getTime() + graceDays * 24 * 60 * 60 * 1000)

    // DUR Koşulu 1: Gelecekteki veya grace süresi dolmamış döngüye GEÇME!
    if (nowDate <= forceDate) {
      break
    }

    // Bu cycle için onaylı ödemeleri ve ödemeyenleri tespit et (Çift sayım engelleme)
    const cycleTx = transactions.filter(t => t.cycle_number === currentCycle)
    // Misafir koltuk (user_id boş) ödemesiyle sender_member_id üzerinden eşleşir.
    const payerKey = (t: LifecycleTransaction) =>
      t.sender_id ?? (t.sender_member_id ? `m:${t.sender_member_id}` : '')
    const memberKey = (m: LifecycleMember) => m.user_id ?? (m.id ? `m:${m.id}` : '')
    const confirmedSenderIds = new Set(
      cycleTx
        .filter(t => t.status === 'confirmed' || t.status === 'auto_confirmed')
        .map(payerKey)
        .filter(Boolean)
    )

    const activeMembers = members.filter(m => m.status === 'active')
    const unpaidMembers = activeMembers.filter(
      m => m.turn_index !== currentCycle && !confirmedSenderIds.has(memberKey(m))
    )

    const totalLateDays = calculateLateDays(nowDate, effectiveDueDate)
    const penalty = totalLateDays <= (3 + graceDays) ? -2 : -5
    const trigger = totalLateDays <= (3 + graceDays) ? 'force_advance_late' : 'force_advance_very_late'

    for (const unpaid of unpaidMembers) {
      if (!unpaid.user_id) {
        if (unpaid.id) unpaidGuestMemberIds.push(unpaid.id)
        continue // misafirin puanı yok
      }
      penalties.push({
        cycleNumber: currentCycle,
        userId: unpaid.user_id,
        penalty,
        trigger,
      })
    }

    for (const t of cycleTx) {
      if (t.id && (t.status === 'pending' || t.status === 'rejected')) {
        idsToSkip.push(t.id)
      }
    }

    // Bir sonraki cycle'a ilerle
    currentCycle += 1
    advancedCyclesCount += 1

    if (currentCycle > memberCount) {
      isCompleted = true
      break
    }

    currentCycleDate = advanceCycleDate(currentCycleDate, cyclePeriod, capaGun)
  }

  return {
    currentCycle,
    currentCycleDate,
    advancedCyclesCount,
    isCompleted,
    penalties,
    idsToSkip,
    unpaidGuestMemberIds,
  }
}
